import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { useRef } from 'react';
import { cn } from '@/lib/cn';

type ParallaxImageProps = {
  src: string;
  alt: string;
  /** Wrapper sizing / aspect classes. */
  className?: string;
  imgClassName?: string;
  /** Parallax intensity (0–0.2 looks best). */
  speed?: number;
  priority?: boolean;
  sizes?: string;
};

/**
 * Image that drifts against the scroll inside a fixed frame.
 * The <img> is scaled up so the vertical travel never reveals an edge.
 */
export function ParallaxImage({
  src,
  alt,
  className,
  imgClassName,
  speed = 0.12,
  priority = false,
  sizes,
}: ParallaxImageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const pct = Math.min(speed, 0.18) * 100;
  const y = useTransform(scrollYProgress, [0, 1], [`-${pct}%`, `${pct}%`]);

  return (
    <div ref={ref} className={cn('relative overflow-hidden bg-paper-deep', className)}>
      <motion.img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        decoding="async"
        sizes={sizes}
        style={reduce ? undefined : { y, scale: 1.28 }}
        className={cn(
          'absolute inset-0 h-full w-full object-cover',
          !reduce && 'will-change-transform',
          imgClassName,
        )}
      />
    </div>
  );
}
